// VERYSIGHT — Central site configuration.
// Update this file to change business details across the entire website.

export const siteConfig = {
  name: "VerySight",
  domain: "verysight.com",
  url: "https://verysight.com",
  tagline: "See Your Business Through Every Financial Lens.",
  description:
    "Strategic Virtual CFO services that transform financial statements into actionable business intelligence — helping owners make smarter, faster and more profitable decisions.",
  // Rotating hero subheadlines
  heroLines: [
    "Strategic Virtual CFO services that transform financial statements into actionable business intelligence — helping owners make smarter, faster and more profitable decisions.",
    "You have revenue but no profit? We show you exactly where it's leaking.",
    "You have profit but no cash? We reveal what's holding your money hostage.",
    "Your annual audit and monthly P&L don't tell the full story. VerySight goes deeper — into the story hiding beneath the numbers.",
    "Planning an IPO, raising private capital, or launching a new project? Your financials must tell a story of performance — not just report numbers.",
  ],
  logo: "https://customer-assets.emergentagent.com/job_ee72b47f-773b-4d57-980c-f1fd9d28a1aa/artifacts/l0v3ln9y_Very%20Sight-%20Logo.jpeg",
  contact: {
    email: "info@verysight.com",
    website: "verysight.com",
    hqLabel: "Dubai · Abu Dhabi · Doha · Riyadh · Bengaluru",
  },
  social: {
    linkedin: "https://www.linkedin.com/company/verysight",
  },
  nav: [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Why Us", href: "#why-us" },
    { label: "Process", href: "#process" },
    { label: "Industries", href: "#industries" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ],
  seo: {
    keywords: [
      "Virtual CFO",
      "Fractional CFO",
      "Virtual CFO UAE",
      "Virtual CFO India",
      "Virtual CFO Saudi Arabia",
      "Financial Consulting",
      "Business Financial Analysis",
      "Cash Flow Analysis",
      "Profitability Analysis",
      "Business Performance Review",
      "Hospitality CFO",
      "Restaurant Financial Consulting",
      "Hotel Financial Consulting",
      "SME Financial Advisory",
      "MSME CFO Services",
    ],
  },
};

export const services = [
  {
    id: 1,
    number: "01",
    title: "Business Financial Health Assessment",
    short: "Independent review of monthly, quarterly, and annual financial performance.",
    bullets: [
      "Monthly, quarterly & annual review",
      "Independent third-party lens",
      "Overall health scoring",
    ],
    span: "lg:col-span-2",
  },
  {
    id: 2,
    number: "02",
    title: "Executive Financial Dashboard Review",
    short: "Complex reports translated into executive insight — understood in minutes.",
    bullets: ["Owner-friendly dashboards", "One-page executive summary", "Boardroom clarity"],
  },
  {
    id: 3,
    number: "03",
    title: "Profitability Analysis",
    short: "Reveal which products, channels and decisions actually create profit.",
    bullets: [
      "High vs low margin products",
      "Hidden cost drivers",
      "Gross, Net & EBITDA trends",
    ],
  },
  {
    id: 4,
    number: "04",
    title: "Cash Flow Intelligence",
    short: "Why is profit up but cash isn't? We answer the questions owners actually ask.",
    bullets: [
      "Working capital pressure",
      "Receivables & payables",
      "Where cash is blocked",
    ],
    span: "lg:col-span-2",
  },
  {
    id: 5,
    number: "05",
    title: "Financial Variance Analysis",
    short: "Budget vs Actual, YoY, QoQ, MoM — with root causes and corrective actions.",
    bullets: ["Budget vs actual", "Year-over-year", "Root-cause commentary"],
  },
  {
    id: 6,
    number: "06",
    title: "Balance Sheet Diagnostics",
    short: "Liquidity, leverage, working capital and capital structure — reviewed with rigor.",
    bullets: [
      "Working capital & liquidity",
      "Receivables, payables & inventory",
      "Debt & capital structure",
    ],
  },
  {
    id: 7,
    number: "07",
    title: "Business Growth Assessment",
    short: "Is your growth profitable, sustainable, efficient and well funded?",
    bullets: ["Profitable vs vanity growth", "Funding health", "Operational scalability"],
  },
  {
    id: 8,
    number: "08",
    title: "Business Risk Assessment",
    short: "Identify financial risks before they become business problems.",
    bullets: [
      "Liquidity, debt & expansion risk",
      "Operational & working-capital risk",
      "Financial sustainability",
    ],
    span: "lg:col-span-2",
  },
  {
    id: 9,
    number: "09",
    title: "Management Performance Insights",
    short: "Board-level commentary: strengths, weaknesses, priorities, recommendations.",
    bullets: [
      "Board-ready commentary",
      "Strategic recommendations",
      "Prioritised action list",
    ],
  },
];

export const traditionalVsVerysight = {
  traditional: {
    title: "Traditional Finance Team",
    subtitle: "Focused on compliance and record-keeping",
    items: [
      "Bookkeeping",
      "VAT & Tax filings",
      "Payroll",
      "Compliance",
      "Statutory audits",
      "Accounting entries",
      "Historical reporting",
    ],
  },
  verysight: {
    title: "VerySight",
    subtitle: "Focused on decisions, performance and business value",
    items: [
      "Business performance",
      "Financial intelligence",
      "Strategic decision making",
      "Cash flow architecture",
      "Profitability engineering",
      "Sustainable growth",
      "Business value creation",
      "Owner-level insights",
      "Financial storytelling",
    ],
  },
};

export const processSteps = [
  {
    n: "01",
    title: "Understand Business",
    desc: "Deep dive into your model, market, ownership and operational rhythm.",
  },
  {
    n: "02",
    title: "Analyze Financial Statements",
    desc: "P&L, Balance Sheet and Cash Flow read with an operator's lens.",
  },
  {
    n: "03",
    title: "Identify Risks & Opportunities",
    desc: "Signals hidden in the numbers — cash traps, margin drift, blind spots.",
  },
  {
    n: "04",
    title: "Prepare Executive Insights",
    desc: "One page of clarity. No jargon. What matters, why it matters, what next.",
  },
  {
    n: "05",
    title: "Present Actionable Recommendations",
    desc: "Sit across the table. Discuss. Debate. Align on the highest leverage moves.",
  },
  {
    n: "06",
    title: "Support Better Business Decisions",
    desc: "An ongoing independent CFO in your corner — every month, every quarter.",
  },
];

export const industries = [
  { name: "Hotels", icon: "Hotel" },
  { name: "Restaurants", icon: "Utensils" },
  { name: "Hospitality", icon: "Wine" },
  { name: "Retail", icon: "ShoppingBag" },
  { name: "Trading", icon: "Truck" },
  { name: "Manufacturing", icon: "Factory" },
  { name: "Professional Services", icon: "Briefcase" },
  { name: "Construction", icon: "HardHat" },
  { name: "Healthcare", icon: "Stethoscope" },
  { name: "Education", icon: "GraduationCap" },
  { name: "Technology", icon: "Cpu" },
];

// Testimonials — sample content, replace names with real client references once collected.
export const testimonials = [
  {
    theme: "P&L Optimization",
    quote:
      "VerySight rebuilt our P&L view outlet-by-outlet. Within one quarter we had lifted gross margin by 3.8% simply by acting on what their analysis surfaced. This is what a real CFO conversation feels like.",
    name: "Rajeev Menon",
    title: "Managing Director",
    company: "Hospitality Group · Dubai",
  },
  {
    theme: "Business Health Diagnosis",
    quote:
      "Their business health diagnosis was the most honest read of our company I have ever received. It confirmed what I suspected and quantified what I couldn't see. We restructured our top three cost centres based on that single review.",
    name: "Aisha Al-Mansouri",
    title: "Chairperson",
    company: "Family Business Group · Riyadh",
  },
  {
    theme: "Cash Flow Allocation & Working Capital Optimization",
    quote:
      "We were profitable but constantly short on cash. VerySight mapped our working capital cycle end-to-end and freed up 42 days of stuck cash across receivables and inventory. It transformed how we allocate money.",
    name: "Karthik Iyer",
    title: "Founder & CEO",
    company: "Cloud Kitchen Chain · Bengaluru",
  },
  {
    theme: "Balance Sheet Deep Dive",
    quote:
      "Their balance sheet deep dive exposed leverage and liquidity risks our auditors had never flagged. We refinanced two facilities on far better terms because we finally understood our own capital structure.",
    name: "Fatima Zahra",
    title: "Group CFO",
    company: "Retail & Trading · Sharjah",
  },
  {
    theme: "Cash Flow Deep Dive",
    quote:
      "The cash flow deep dive was a revelation. VerySight showed us the exact three line items eating our cash every month — none of which were on any dashboard we had. That report paid for itself in the first thirty days.",
    name: "Vikram Shetty",
    title: "Managing Partner",
    company: "Restaurant Group · Bengaluru",
  },
  {
    theme: "Financial Modelling",
    quote:
      "Before opening our fourth hotel we asked VerySight to model the deal. Their financial model reshaped the entire negotiation — cap-ex, payback, downside scenarios. We walked into the boardroom with genuine conviction.",
    name: "Omar Al-Rashid",
    title: "Chief Executive Officer",
    company: "Hotel & Resort Group · Abu Dhabi",
  },
  {
    theme: "P&L Optimization + Financial Modelling",
    quote:
      "Combining a P&L review with a three-year forward model gave our board the clearest strategic picture we've had in a decade. VerySight doesn't just report the past — they help you build the future.",
    name: "Priya Krishnan",
    title: "Executive Director",
    company: "Manufacturing & Trading · Chennai",
  },
  {
    theme: "Business Health Diagnosis + Working Capital",
    quote:
      "A full business health diagnosis followed by a working capital reset — VerySight delivered both inside eight weeks. Our cash conversion cycle went from 71 days to 34 days. Independent, sharp, no jargon.",
    name: "Hassan Al-Suwaidi",
    title: "Founder & Chairman",
    company: "Trading & Distribution · Riyadh",
  },
];

export const faqs = [
  {
    q: "How is a Virtual CFO different from my accountant or finance manager?",
    a: "Your accountant records what happened. A Virtual CFO explains what those numbers mean for the business and helps you decide what to do next. We focus on strategy, performance, cash and value — not compliance.",
  },
  {
    q: "Do you replace my existing finance team?",
    a: "No. VerySight works alongside your existing team. They continue to handle bookkeeping, VAT and compliance. We provide an independent, owner-facing layer of financial intelligence on top.",
  },
  {
    q: "Which countries and regions do you serve?",
    a: "We work with businesses across India, the UAE, Saudi Arabia and the wider Middle East. Engagements are delivered virtually with periodic on-site reviews where required.",
  },
  {
    q: "Which industries do you specialise in?",
    a: "Hospitality (hotels, restaurants, cloud kitchens, cafés), retail, trading, manufacturing, professional services, construction, healthcare, education and technology — with deep experience in owner-managed and family businesses.",
  },
  {
    q: "What size of business is a good fit?",
    a: "Typically SMEs, MSMEs and mid-market businesses with annual revenue between USD 1M and USD 100M — where the founder still influences most decisions but reporting has become complex.",
  },
  {
    q: "How is your engagement priced?",
    a: "We offer three formats: a one-time Financial Health Assessment, a monthly retainer for ongoing Virtual CFO oversight, and project-based reviews (e.g. pre-investment, pre-expansion). Pricing is discussed after a free consultation.",
  },
  {
    q: "What information do you need from us to begin?",
    a: "Typically the last 12–24 months of financial statements, a chart of accounts, management reports if available, and a 30-minute conversation with the owner. Everything is treated under strict confidentiality.",
  },
  {
    q: "Is my financial data confidential and secure?",
    a: "Absolutely. All engagements begin with a mutual NDA. Data is exchanged via encrypted channels and access is restricted to the specific advisors assigned to your engagement.",
  },
  {
    q: "Do you provide audit, tax or bookkeeping services?",
    a: "No. VerySight deliberately does not offer audit, statutory tax filings or bookkeeping. This keeps our advisory voice independent — free from the incentives of compliance work.",
  },
  {
    q: "How quickly can we get started?",
    a: "Most engagements begin within 5–7 working days of the initial consultation. A first executive insight report can typically be delivered in 2–3 weeks.",
  },
];
